import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation, useMatch } from 'react-router-dom';
import { Slide, toast } from 'react-toastify';
import Header from './components/Header';
import Tabs from './components/Tabs';
import Buttons from './components/Buttons';
import PropertyDetailsTab from './components/PropertyDetailsTab';
import FilesTab from './components/FilesTab';
import { AddEdit, PropertyContactDetail, PropertyUnitType } from '../../../interfaces/property.interface';
import { Validation } from '../../../interfaces/common';
import {
  AuthRoutes,
  Modules,
  RouteParamsTypes,
} from '../../../enums';
import {
  APP_SUCCESS_MESSAGES,
  APP_ERROR_MESSAGES,
  GENERIC_LOOKUP_KEYS,
  VALIDATION_FIELD_MAPPING,
} from '../../../utils/appConstant';
import { validateAddEditForm } from './validations';
import useFetch from '../../../hooks/useFetch';
import PropertyService from '../../../services/property.service';
import CommonService from '../../../services/common.service';
import DeveloperService from '../../../services/developer.service';
import { formatDate, parseDateToLocal } from '../../../utils/methods';

import Spinner from '../../../components/Spinner';
import Toast from '../../../components/Toast';
import { STATUS_CODE } from '../../../utils/apiConstant';

const initialFormData: AddEdit = {
  name: '',
  code: '',
  description: '',
  category_id: null,
  construction_status: null,
  approval_status: null,
  developer_property: [],
  rera_number: '',
  launch_date: null,
  possession_date: null,
  address_line_1: '',
  address_line_2: '',
  country_id: null,
  state_id: null,
  city_id: null,
  area_id: '',
  area: [],
  pincode: '',
  latitude: '',
  longitude: '',
  property_contact_details: [],
  total_blocks: 0,
  total_flats: 0,
  total_shops: 0,
  property_unit_types: [],
  amenities: [],
  is_active: true,
  is_featured: false,
  property_developer_name: '',
  whatsapp_description: '',
  documents: [],
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  category: '',
  construction_status: '',
  approval_status: '',
  developer: '',
  country: '',
  state: '',
  city: '',
  amenities: '',
  area: '',
  nearbyArea: '',
  property_type: '',
  bhk_type: '',
  measurement: '',
};

const AddEditView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const idRef = useRef(id);
  const isActiveParam = !id ? 1 : undefined;

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const location = useLocation();
  const isView = !!useMatch(Modules.properties + AuthRoutes.properties + RouteParamsTypes.view + '/:id');
  const isEdit = !!useMatch(Modules.properties + AuthRoutes.properties + RouteParamsTypes.edit + '/:id');

  const [tab, setTab] = useState<string>('propertyDetails');
  const [loading, setLoading] = useState<boolean>(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [formData, setFormData] = useState<AddEdit>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);

  useEffect(() => {
    setTab(location?.state?.tab ?? 'propertyDetails');
  }, [location.state, location.key]);

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
  }, [location.key]);

  // Fetch documents function
  const fetchDocuments = useCallback((propertyId: string, clearLocal: boolean = false) => {
    if (!propertyId) return;

    PropertyService.getDocuments(propertyId).then((res: any) => {
      if (res?.doc_types && Array.isArray(res.doc_types)) {
        const docs = res.doc_types;
        const primary = docs.find((d: any) => d.doc_type === 'primary_image');
        const whatsapp = docs.find((d: any) => d.doc_type === 'whatsapp_image');
        const brochure = docs.find((d: any) => d.doc_type === 'brochure');
        const gallery = docs.find((d: any) => d.doc_type === 'gallery')?.documents || [];

        // Extract other documents
        const knownTypes = ['primary_image', 'whatsapp_image', 'brochure', 'gallery'];
        const otherDocsGroups = docs.filter((d: any) => !knownTypes.includes(d.doc_type));
        let otherDocuments: any[] = [];
        otherDocsGroups.forEach((group: any) => {
            if (group.documents && Array.isArray(group.documents)) {
                const docsWithType = group.documents.map((d: any) => ({ ...d, doc_type: group.doc_type }));
                otherDocuments = [...otherDocuments, ...docsWithType];
            }
        });

        setFormData((prev) => {
           // Preserve locally added gallery files (instanceof File)
           const localGalleryFiles = !clearLocal && Array.isArray(prev.gallery) ? prev.gallery.filter((item: any) => item instanceof File) : [];
           
           // Preserve locally added document files (objects with file property)
           const localDocFiles = !clearLocal && Array.isArray(prev.documents) ? prev.documents.filter((item: any) => item.file instanceof File) : [];

           // Combine fetched DB files with local files
           const mergedGallery = [...gallery, ...localGalleryFiles];
           const mergedDocuments = [...otherDocuments, ...localDocFiles];

           // Preserve unsaved single files if they exist locally as File objects
           const currentPrimary = !clearLocal && prev.primary_image instanceof File ? prev.primary_image : (primary || null);
           const currentWhatsapp = !clearLocal && prev.whatsapp_image instanceof File ? prev.whatsapp_image : (whatsapp || null);
           const currentBrochure = !clearLocal && prev.brochure instanceof File ? prev.brochure : (brochure || null);

           return {
              ...prev,
              primary_image: currentPrimary,
              whatsapp_image: currentWhatsapp,
              brochure: currentBrochure,
              gallery: mergedGallery,
              documents: mergedDocuments,
           };
        });
      }
    }).catch((err: any) => {
      console.error("Failed to fetch documents:", err);
    });
  }, []);

  // Fetch Property Data (Edit/View)
  const { loading: getContentLoading } = useFetch({
    service: PropertyService,
    func: 'get',
    payload: id,
    skip: !id,
    dependency: [id, location.key],
    onSuccess: (data: any) => {
      if (idRef.current !== id) return;
      const mappedData = {
        ...data,
        // Ensure IDs are extracted if fields are returned as objects
        category_id: data.category_id || (data.category?.id) || null,
        construction_status: (typeof data.construction_status === 'object' && data.construction_status !== null) ? data.construction_status.id : (data.construction_status || data.construction_status_id || data.constructionStatus || null),
        approval_status: (typeof data.approval_status === 'object' && data.approval_status !== null) ? data.approval_status.id : (data.approval_status || data.approval_status_id || data.approvalStatus || null),
        country_id: data.country_id || (data.country?.id) || null,
        state_id: data.state_id || (data.state?.id) || null,
        city_id: data.city_id || (data.city?.id) || null,

        // Handle Developer mapping (could be objects or IDs)
        developer_property: data.developer_property
          ? data.developer_property.map((d: any) => (typeof d === 'object' ? String(d.id || d.developer_id) : String(d)))
          : (data.developer_property_ids ? data.developer_property_ids.map((id: any) => String(id)) : []),

        // Handle Amenities mapping
        amenities: data.amenities
          ? data.amenities.map((a: any) => (typeof a === 'object' ? String(a.id || a.amenity_id) : String(a)))
          : (data.amenity_property_ids ? data.amenity_property_ids.map((id: any) => String(id)) : []),

        // Handle Area mapping (take first if array)
        // Handle Area mapping (Single)
        area_id: data.area_id ? String(data.area_id) : '',

        // NEW: Multi-select support (Nearby Areas)
        area: data.area_ids
          ? data.area_ids.map((id: any) => String(id))
          : (data.property_area_ids ? data.property_area_ids.map((id: any) => String(id)) : []),

        // Handle Contact Details (map contact_email -> email)
        property_contact_details: data.property_contact_details
          ? data.property_contact_details.map((c: any) => ({
            ...c,
            email: c.contact_email || c.email // Map API contact_email back to UI email
          }))
          : [],

        property_unit_types: data.property_unit_types || [],
        // Map dates
        launch_date: data.launch_date,
        possession_date: data.possession_date,
        code: data.code || '',
        latitude: (data.latitude && Number(data.latitude) !== 0) ? data.latitude : '',
        longitude: (data.longitude && Number(data.longitude) !== 0) ? data.longitude : '',
      };

      setFormData(mappedData);

      if (id) {
        fetchDocuments(id);
      }
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.properties + AuthRoutes.properties);
    },
  });

  // Lookups
  const { data: allCategories } = useFetch({
    service: CommonService,
    func: 'categoryLookup',
    payload: { searchText: '', is_active: isActiveParam },
    dependency: [isActiveParam],
  });

  const { data: categories } = useFetch({
    service: CommonService,
    func: 'categoryLookup',
    payload: { searchText: dropdownSearchData.category, is_active: isActiveParam },
    dependency: [dropdownSearchData.category, isActiveParam],
  });

  const { data: allConstructionStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.constructionStatus, searchText: '' },
    dependency: [],
  });

  const { data: constructionStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.constructionStatus, searchText: dropdownSearchData.construction_status },
    dependency: [dropdownSearchData.construction_status],
  });

  const { data: allApprovalStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.approvalStatus, searchText: '' },
    dependency: [],
  });

  const { data: approvalStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.approvalStatus, searchText: dropdownSearchData.approval_status },
    dependency: [dropdownSearchData.approval_status],
  });

  const { data: allDevelopers } = useFetch({
    service: DeveloperService,
    func: 'lookup',
    payload: { searchText: '', is_active: isActiveParam },
    dependency: [isActiveParam],
  });

  const { data: developers } = useFetch({
    service: DeveloperService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.developer, is_active: isActiveParam },
    dependency: [dropdownSearchData.developer, isActiveParam],
  });

  const { data: allCountries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: '' },
    dependency: [],
  });

  const { data: countries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: dropdownSearchData.country },
    dependency: [dropdownSearchData.country],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '', countryId: formData.country_id?.toString() || '1' },
    dependency: [formData.country_id],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: dropdownSearchData.state || '', countryId: formData.country_id?.toString() || '1' },
    dependency: [dropdownSearchData.state, formData.country_id],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '', stateId: formData.state_id?.toString() },
    dependency: [formData.state_id],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.city || '', stateId: formData.state_id?.toString() },
    dependency: [dropdownSearchData.city, formData.state_id],
  });

  const { data: allAreas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: '',
      cityId: formData.city_id,
      is_active: isActiveParam
    },
    dependency: [formData.city_id, isActiveParam],
  });

  const { data: allNearbyAreas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: '',
      cityId: formData.city_id,
      is_active: isActiveParam
    },
    dependency: [formData.city_id, isActiveParam],
  });

  const { data: areas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: dropdownSearchData.area || '',
      cityId: formData.city_id,
      is_active: isActiveParam
    },
    dependency: [dropdownSearchData.area, formData.city_id, isActiveParam],
  });

  const { data: nearbyAreas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: dropdownSearchData.nearbyArea || '',
      cityId: formData.city_id,
      is_active: isActiveParam
    },
    dependency: [dropdownSearchData.nearbyArea, formData.city_id, isActiveParam],
  });

  useEffect(() => {
    if (!id && allCountries?.length > 0 && !formData.country_id) {
      const india = allCountries.find(
        (country: any) => country.name.toLowerCase() === 'india'
      );
      if (india) {
        setFormData((prev) => ({
          ...prev,
          country_id: india.id,
        }));
      }
    }
  }, [allCountries, id, formData.country_id]);

  useEffect(() => {
    if (!id && allStates?.length > 0 && !formData.state_id) {
      const gujarat = allStates.find(
        (state: any) => state.name.toLowerCase() === 'gujarat'
      );
      if (gujarat) {
        setFormData((prev) => ({
          ...prev,
          state_id: gujarat.id,
        }));
      }
    }
  }, [allStates, id, formData.state_id]);

  // Property Types and BHK Types
  const { data: allPropertyTypes } = useFetch({
    service: CommonService,
    func: 'propertyTypeLookup',
    payload: { searchText: '', is_active: isActiveParam },
    dependency: [isActiveParam],
  });

  const { data: propertyTypes } = useFetch({
    service: CommonService,
    func: 'propertyTypeLookup',
    payload: { searchText: dropdownSearchData.property_type, is_active: isActiveParam },
    dependency: [dropdownSearchData.property_type, isActiveParam],
  });

  const { data: allBhkTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.bhkType, searchText: '' }, 
    dependency: [],
  });

  const { data: bhkTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.bhkType, searchText: dropdownSearchData.bhk_type }, 
    dependency: [dropdownSearchData.bhk_type],
  });

  const { data: allMeasurements } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: 'measurement', searchText: '' }, 
    dependency: [],
  });

  const { data: measurements } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: 'measurement', searchText: dropdownSearchData.measurement }, 
    dependency: [dropdownSearchData.measurement],
  });

  // Amenities (generic lookup? or Amenities service if exists. Using generic for now)
  const { data: allAmenities } = useFetch({
    service: CommonService,
    func: 'amenityLookup',
    payload: { searchText: '', is_active: isActiveParam },
    dependency: [isActiveParam],
  });

  const { data: amenities } = useFetch({
    service: CommonService,
    func: 'amenityLookup',
    payload: { searchText: dropdownSearchData.amenities, is_active: isActiveParam },
    dependency: [dropdownSearchData.amenities, isActiveParam],
  });


  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => ({
        ...prev,
        [name]: event.target.value,
      }));
    },
    []
  );

  const handleTabChange = useCallback((e: any, value: any) => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback((event: any, name: string) => {
    let value: any;
    if (name === 'is_active' || name === 'is_featured') {
      value = event?.target?.checked;
    } else {
      value = (event && event.target) ? event.target.value : event;
    }

    setFormData((prev) => {
      let newData = { ...prev, [name]: value };

      // Cascading logic
      if (name === 'country_id' || name === VALIDATION_FIELD_MAPPING.countryId) {
        newData.state_id = null;
        newData.city_id = null;
        newData.area_id = '';
        newData.area = [];
      }
      if (name === 'state_id' || name === VALIDATION_FIELD_MAPPING.stateId) {
        newData.city_id = null;
        newData.area_id = '';
        newData.area = [];
      }
      if (name === 'city_id' || name === VALIDATION_FIELD_MAPPING.cityId) {
        newData.area_id = '';
        newData.area = [];
      }

      return newData;
    });

    // Clear validation errors for documents when updated
    if (name === 'documents' && Array.isArray(value)) {
      setValidation((prev) => {
        // Keep non-document errors
        const nonDocumentErrors = prev.fields.filter(field => !field.field.startsWith('documents-'));
        
        // Check which document errors should be kept (documents without doc_type)
        const documentErrors = prev.fields.filter(field => field.field.startsWith('documents-'));
        const remainingDocumentErrors = documentErrors.filter((error) => {
          // Extract index from error field like "documents-0-doc_type"
          const match = error.field.match(/^documents-(\d+)-doc_type$/);
          if (match) {
            const index = parseInt(match[1], 10);
            // Keep error if document at this index doesn't have doc_type
            return !value[index]?.doc_type;
          }
          return false;
        });

        const newFields = [...nonDocumentErrors, ...remainingDocumentErrors];
        return {
          validate: newFields.length === 0,
          fields: newFields
        };
      });
    }
  }, []);

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: event.target?.value?.trim ? event.target.value.trim() : event.target.value,
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );



  const handlePropertiesContactChange = useCallback((data: PropertyContactDetail[]) => {
    setFormData(prev => ({ ...prev, property_contact_details: data }));
  }, []);

  const handleInventoryChange = useCallback((data: PropertyUnitType[]) => {
    setFormData(prev => ({ ...prev, property_unit_types: data }));
  }, []);

  const handleSubmit = useCallback(async (action: '1' | '2', event?: React.FormEvent) => {
    event && event.preventDefault();

    const newValidation = validateAddEditForm(formData);
    setValidation(newValidation);
    if (!newValidation.validate) return;

    if (action === '1') setLoading(true);
    else setActionTwoLoading(true);

    try {
      const payload = {
        ...formData,
        category_id: Number(formData.category_id),
        state_id: Number(formData.state_id),
        city_id: Number(formData.city_id),
        country_id: Number(formData.country_id),
        area_id: formData.area_id ? Number(formData.area_id) : null,
        construction_status: formData.construction_status,
        approval_status: formData.approval_status,

        developer_property_ids: formData.developer_property ? formData.developer_property.map((d: string) => Number(d)) : [],
        amenity_property_ids: formData.amenities ? formData.amenities.map((a: string) => Number(a)) : [],
        property_area_ids: formData.area ? formData.area.map((a: string) => Number(a)) : [],

        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        launch_date: formData.launch_date && parseDateToLocal(formData.launch_date) ? formatDate(parseDateToLocal(formData.launch_date)!) : null,
        possession_date: formData.possession_date && parseDateToLocal(formData.possession_date) ? formatDate(parseDateToLocal(formData.possession_date)!) : null,

        property_contact_details: formData.property_contact_details.map((c: any) => ({
          contact_person: c.contact_person,
          contact_number: c.contact_number,
          contact_email: c.email,
          is_primary: c.is_primary,
          notes: c.notes
        })),

        property_unit_types: formData.property_unit_types.map((u: any) => ({
          ...u,
          property_type_id: Number(u.property_type_id),
          price: Number(u.price),
          rera_area: u.rera_area ? Number(u.rera_area) : null,
          measurement: u.measurement ? u.measurement : null,
          total_units: Number(u.total_units)
        })),
      } as any;

      // Remove file objects from payload as they are uploaded separately
      delete payload.developer_property;
      delete payload.amenities;
      // delete payload.area_id; // Keeping area_id as it is now required
      delete payload.area;
      delete payload.primary_image;
      delete payload.whatsapp_image;
      delete payload.brochure;
      delete payload.gallery;
      delete payload.documents;

      // Phase 1: Save/Update Property Text Data
      let response: any;
      let propertyId = id;

      if (id && isEdit) {
        response = await PropertyService.edit(id, payload);
      } else {
        response = await PropertyService.add(payload);
        if (response?.id) propertyId = response.id;
        else if (response?.data?.id) propertyId = response.data.id;
      }

      if (response.success !== false) {
        // Phase 2: Upload Files if property saved successfully
        const uploadPromises: Promise<any>[] = [];

        // Helper for sequential or parallel uploads
        const { ChunkUploadService } = await import('../../../services/chunkUpload.service');

        // 1. Primary Image
        if (formData.primary_image instanceof File && propertyId) {
          uploadPromises.push(ChunkUploadService.uploadFile(formData.primary_image, propertyId, 'primary_image'));
        }
        // 2. Whatsapp Image
        if (formData.whatsapp_image instanceof File && propertyId) {
          uploadPromises.push(ChunkUploadService.uploadFile(formData.whatsapp_image, propertyId, 'whatsapp_image'));
        }
        // 3. Brochure
        if (formData.brochure instanceof File && propertyId) {
          uploadPromises.push(ChunkUploadService.uploadFile(formData.brochure, propertyId, 'brochure'));
        }
        // 4. Gallery Images
        if (formData.gallery && Array.isArray(formData.gallery) && propertyId) {
          for (const file of formData.gallery) {
            if (file instanceof File) {
              uploadPromises.push(ChunkUploadService.uploadFile(file, propertyId, 'gallery'));
            }
          }
        }

        // 5. Other Documents
        if (formData.documents && Array.isArray(formData.documents) && propertyId) {
            for (const item of formData.documents) {
                if (item.file instanceof File) {
                    const docType = item.doc_type || 'document'; 
                    uploadPromises.push(ChunkUploadService.uploadFile(item.file, propertyId, docType));
                }
            }
        }

        if (uploadPromises.length > 0) {
          // You might want to show a specific "Uploading files..." toast here
          await Promise.allSettled(uploadPromises);
        }

        // Success Navigation
        toast.success(
          <Toast
            message={isEdit ? APP_SUCCESS_MESSAGES.propertyUpdated : APP_SUCCESS_MESSAGES.propertyCreated}
          />,
          { icon: false }
        );

        if (isEdit) {
          if (tab === 'files' && action === '1' && propertyId) {
             fetchDocuments(propertyId, true);
          } else {
             navigate(Modules.properties + AuthRoutes.properties);
          }
        } else if (action === '2') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setFormData(initialFormData);
          setValidation(initialValidation);
          setTab('propertyDetails');
          navigate(Modules.properties + AuthRoutes.properties + RouteParamsTypes.add, { replace: true });
        } else {
            // New Property Created (Action 1) -> Redirect to Edit (Basic Details by default, or Files?)
            // If user was on basic details and created property, redirect to edit view.
            // But usually we redirect to list.
            // Wait, existing logic said:
            // New Property Created (Action 1) -> Redirect to Edit (Files Tab)
            navigate(Modules.properties + AuthRoutes.properties + RouteParamsTypes.edit + '/' + propertyId, { state: { tab: 'files' } });
        }
      }
    } catch (error) {
      console.error("Save failed", error);
      // Error handled by interceptor
    } finally {
      if (action === '1') setLoading(false);
      else setActionTwoLoading(false);
    }
  }, [id, isEdit, formData, navigate, t]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );


  const errors = useMemo(() => {
    return validation.fields.reduce((acc: any, field) => {
      acc[field.field] = t(field.message);
      return acc;
    }, {});
  }, [validation, t]);

  let formContent;

  // Reuse the same loading check as other modules if available, else just render
  // Note: 'loading' here refers to submit loading. We need fetch loading.
  // We can use a unified loading state or just check if id exists and data is not yet set? 
  // User/Dev module has 'getContentLoading' from useFetch. We should add that alias.
  // For now, assuming form data is ready or we are in 'Add' mode.

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'propertyDetails') {
    formContent = (
      <PropertyDetailsTab
        formData={formData}
        handleChange={handleInputChange}
        handlePropertiesContactChange={handlePropertiesContactChange}
        handleInventoryChange={handleInventoryChange}
        errors={errors}
        view={isView}
        categories={categories}
        allCategories={allCategories}
        constructionStatus={constructionStatus}
        allConstructionStatus={allConstructionStatus}
        developers={developers}
        allDevelopers={allDevelopers}
        countries={countries}
        allCountries={allCountries}
        states={states}
        allStates={allStates}
        cities={cities}
        allCities={allCities}
        propertyTypes={propertyTypes}
        allPropertyTypes={allPropertyTypes}
        bhkTypes={bhkTypes}
        allBhkTypes={allBhkTypes}
        measurements={measurements}
        allMeasurements={allMeasurements}
        amenities={amenities}
        allAmenities={allAmenities}
        areas={areas}
        nearbyAreas={nearbyAreas}
        allAreas={allAreas}
        allNearbyAreas={allNearbyAreas}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        dropdownSearchData={dropdownSearchData}
        onInputBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        approvalStatus={approvalStatus}
        allApprovalStatus={allApprovalStatus}
      />
    );
  } else if (tab === 'files') {
    formContent = (
      <FilesTab
        formData={formData}
        onChange={handleInputChange}
        view={isView}
        onRefresh={() => id && fetchDocuments(id)}
        errors={errors}
      />
    );
  }

  return (
    <>
      <Header
        edit={isEdit}
        view={isView}
        handleEditButtonClick={(e?: any) => {
            const url = Modules.properties + AuthRoutes.properties + RouteParamsTypes.edit + '/' + id;
            if (e && (e.ctrlKey || e.metaKey)) {
                window.open(url, '_blank');
            } else {
                navigate(url);
            }
        }}
      />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('properties.addEdit.pageTexts.title.addTitle')
            : isView
              ? t('properties.addEdit.pageTexts.title.viewTitle')
              : t('properties.addEdit.pageTexts.title.editTitle')}
        </h5>

        <hr className="m-0"></hr>

        <Tabs
          edit={isEdit}
          view={isView}
          tab={tab}
          onTabChange={handleTabChange}
          filesDisabled={!id}
        />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          edit={isEdit}
          view={isView}
          onCancel={() => navigate(Modules.properties + AuthRoutes.properties)}
          hideActionTwo={tab === 'files'}
          loading={loading}
          actionTwoLoading={actionTwoLoading}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
};

export default AddEditView;
